/**
 * Digital Ocean Platform - One-Click Connect
 */
import axios from 'axios'
import { AlertCircle, Check, Cloud, Globe, Loader2, Plus, RefreshCw, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface DOAccount {
  id: number
  label: string
  droplets_count: number
  spaces_count: number
  apps_count: number
  status: string
  connected_at: string
}

export default function DigitalOceanPage() {
  const [accounts, setAccounts] = useState<DOAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [token, setToken] = useState('')
  const [label, setLabel] = useState('')

  const fetchAccounts = async () => {
    try {
      const jwt = localStorage.getItem('token')
      const resp = await axios.get<{ accounts: DOAccount[] }>(`${API_URL}/api/aicloud/digitalocean/accounts`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      setAccounts(resp.data.accounts ?? [])
    } catch {
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchAccounts() }, [])

  const handleConnect = async () => {
    if (!token) {
      setError('Personal Access Token is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const jwt = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/digitalocean/connect`, { token, label }, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      setSuccess('Digital Ocean account connected')
      setShowAdd(false)
      setToken(''); setLabel('')
      void fetchAccounts()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to connect')
    } finally {
      setSaving(false)
    }
  }

  const handleSync = async (id: number) => {
    try {
      const jwt = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/digitalocean/accounts/${id}/sync`, {}, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      void fetchAccounts()
    } catch {
      setError('Sync failed')
    }
  }

  const handleDisconnect = async (id: number) => {
    try {
      const jwt = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/digitalocean/accounts/${id}`, {
        headers: { Authorization: `Bearer ${jwt}` }
      })
      setAccounts(prev => prev.filter(a => a.id !== id))
    } catch {
      setError('Failed to disconnect')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Digital Ocean</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            One-click connection for Droplets, Spaces, and Apps.
          </p>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#008bcf', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <Plus size={14} /> Connect DO
          </button>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}>
          <Check size={14} /> {success}
        </div>
      )}

      {showAdd && (
        <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '14px', marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Personal Access Token</label>
            <input type="password" placeholder="dop_v1_..." value={token}
              onChange={e => setToken(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }} />
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '6px' }}>
              Create a token at DO Dashboard &rarr; API &rarr; Personal Access Tokens (Select &apos;All Scopes&apos;)
            </div>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Label (optional)</label>
            <input type="text" placeholder="e.g. Production DO" value={label}
              onChange={e => setLabel(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => void handleConnect()} disabled={saving}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', background: '#008bcf', color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Zap size={14} />} 
              {saving ? 'Connecting...' : 'Connect Digital Ocean'}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '10px 20px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: '13px' }}>Loading accounts...</div>
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '14px' }}>
          <Cloud size={32} color="#008bcf" style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px' }}>No Digital Ocean accounts connected yet.</p>
          <button onClick={() => setShowAdd(true)} style={{ background: '#008bcf', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Get Started
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '14px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(0,139,207,0.05) 0%, transparent 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(0,139,207,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Globe size={18} color="#008bcf" />
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 700 }}>{acc.label || 'DO Account'}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Connected {new Date(acc.connected_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => void handleSync(acc.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '12px' }}>
                    <RefreshCw size={12} /> Sync
                  </button>
                  <button onClick={() => void handleDisconnect(acc.id)} style={{ padding: '6px', color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '16px 20px' }}>
                {[
                  { label: 'Droplets', count: acc.droplets_count, color: '#008bcf' },
                  { label: 'Spaces', count: acc.spaces_count, color: '#10b981' },
                  { label: 'Apps', count: acc.apps_count, color: '#8b5cf6' },
                ].map(r => (
                  <div key={r.label} style={{ padding: '12px', background: 'var(--body-bg)', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: r.color }}>{r.count}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{r.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
